// src/pages/CalendarView.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useApi } from "../apiClient";
import { motion, AnimatePresence } from "framer-motion";
import formatDate from "../utils/formatDate";
import EditActivityModal from "../components/EditActivityModal";
import toast from "react-hot-toast";
import { useLang } from "../contexts/LanguageContext";
import locales from "../utils/locales";

// category color
const categories = {
  work: "bg-blue-400",
  family: "bg-pink-400",
  health: "bg-green-400",
  learning: "bg-yellow-400",
  rest: "bg-gray-400",
  other: "bg-purple-400"
};

// Category label multi-lang
const categoryOptions = [
  { value: "work", label: { en: "Work", th: "งาน" } },
  { value: "family", label: { en: "Family", th: "ครอบครัว" } },
  { value: "health", label: { en: "Health", th: "สุขภาพ" } },
  { value: "learning", label: { en: "Learning", th: "การเรียนรู้" } },
  { value: "rest", label: { en: "Rest", th: "พักผ่อน" } },
  { value: "other", label: { en: "Other", th: "อื่น ๆ" } }
];

// Helper: get weekdays in current language
function getWeekdays(t) {
  return [t.mon, t.tue, t.wed, t.thu, t.fri, t.sat, t.sun];
}

// Get week dates
function getWeekDates(date) {
  const result = [];
  const curr = new Date(date);
  let day = curr.getDay();
  if (day === 0) day = 7;
  curr.setDate(curr.getDate() - (day - 1));
  for (let i = 0; i < 7; i++) {
    result.push(new Date(curr));
    curr.setDate(curr.getDate() + 1);
  }
  return result;
}

export default function CalendarView() {
  const api = useApi();
  const { lang } = useLang();
  const t = locales[lang];
  const WEEKDAYS = getWeekdays(t);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editActivity, setEditActivity] = useState(null);


  // Mini calendar
  const [showMini, setShowMini] = useState(false);
  const calendarRef = useRef(null);

  useEffect(() => {
    function onScroll() {
      const y = calendarRef.current ? calendarRef.current.getBoundingClientRect().top : 0;
      setShowMini(y < 60);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const reloadActivities = useCallback(async () => {
    setLoading(true);
    const day = selectedDate.toLocaleDateString("en-CA");
    try {
      const data = await api.activities.list({ from: day, to: day });
      setActivities(data || []);
    } catch {
      setActivities([]);
    }
    setLoading(false);
  }, [selectedDate, api]);

  useEffect(() => {
    reloadActivities();
  }, [reloadActivities]);

  // Add Activity
  const handleAddActivity = async (newData) => {
    await api.activities.create({
      ...newData,
      activity_date: selectedDate.toLocaleDateString("en-CA"),
    });
    setShowAddModal(false);
    reloadActivities();
    toast.success(t.added);
  };

  // Edit Activity
  const handleSaveEditActivity = async (updatedData) => {
    await api.activities.update(updatedData.id, {
      activity: updatedData.activity,
      duration: updatedData.duration,
      note: updatedData.note,
      mood: updatedData.mood,
      category: updatedData.category,
      activity_date: updatedData.activity_date,
      start_time: updatedData.start_time,
      end_time: updatedData.end_time,
    });
    setShowEditModal(false);
    setEditActivity(null);
    reloadActivities();
    toast.success(t.updated);
  };

  const handleDelete = async (activity) => {
    if (!window.confirm(t.confirm_delete)) return;
    await api.activities.delete(activity.id);
    reloadActivities();
    toast.success(t.deleted);
  };

  const handleOpenEdit = (activity) => {
    setEditActivity(activity);
    setShowEditModal(true);
  };

  const weekDates = useMemo(() => getWeekDates(selectedDate), [selectedDate]);

  return (
    <div className="max-w-4xl mx-auto p-4 mt-0 flex flex-col md:flex-row gap-4">
      {/* Mini Calendar Sticky */}
      {showMini && (
        <div
          className="fixed left-0 right-0 z-40 bg-white border-b px-2 py-1 shadow flex justify-center"
          style={{ top: 0 }}
        >
          {weekDates.map((d, i) => (
            <button
              key={i}
              onClick={() => setSelectedDate(d)}
              className={`flex flex-col items-center mx-1 px-1 ${
                d.toLocaleDateString("en-CA") === selectedDate.toLocaleDateString("en-CA")
                  ? "text-white bg-indigo-500 rounded font-bold"
                  : "text-gray-700"
              }`}
              style={{ width: 38, height: 44 }}
            >
              <span className="text-xs">{WEEKDAYS[i]}</span>
              <span className="text-lg">{d.getDate()}</span>
            </button>
          ))}
        </div>
      )}

      {/* Calendar */}
      <div className="md:w-1/2 w-full flex flex-col items-center" ref={calendarRef}>
        <Calendar
          onChange={setSelectedDate}
          value={selectedDate}
          calendarName="rounded-xl border shadow"
          locale={lang === "th" ? "th-TH" : "en-GB"}
        />
        <button
          className="mt-4 bg-gradient-to-r from-pink-500 to-blue-400 text-white px-4 py-2 rounded-xl font-bold shadow hover:scale-105 transition"
          onClick={() => setShowAddModal(true)}
        >
          {t.add_activity}
        </button>
      </div>

      {/* Activities */}
      <div className="md:w-1/2 w-full pt-2">
        <h2 className="font-bold text-xl mb-4">{t.activities}</h2>
        {loading && <div className="text-gray-500">{t.loading}</div>}
        {!loading && activities.length === 0 && (
          <div className="text-gray-400 mb-4">{t.no_activities_today}</div>
        )}
        <motion.ul
          className="space-y-3"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0, y: 12 },
            visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.08 } }
          }}
        >
          {activities.map((a) => {
            const catObj = categoryOptions.find(c => c.value === a.category);
            return (
              <motion.li
                key={a.id}
                className="bg-white rounded-xl shadow p-4 group"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex items-center gap-2">
                  {/* Mood Emoji */}
                  {a.mood && (
                    <span
                      className="text-2xl"
                      title={
                        a.mood === "😊" ? t.mood_happy :
                        a.mood === "😌" ? t.mood_calm :
                        a.mood === "😐" ? t.mood_neutral :
                        a.mood === "😔" ? t.mood_sad :
                        a.mood === "😠" ? t.mood_angry : ""
                      }
                      aria-label={
                        a.mood === "😊" ? t.mood_happy :
                        a.mood === "😌" ? t.mood_calm :
                        a.mood === "😐" ? t.mood_neutral :
                        a.mood === "😔" ? t.mood_sad :
                        a.mood === "😠" ? t.mood_angry : ""
                      }
                      style={{ minWidth: 32, textAlign: "center" }}
                    >
                      {a.mood}
                    </span>
                  )}
                  <span className="font-bold">{a.activity}</span>
                  {a.category && (
                    <span className={`text-xs px-2 rounded ${categories[a.category] || "bg-gray-300"} text-white`}>
                      {catObj ? catObj.label[lang] : a.category}
                    </span>
                  )}
                  {a.is_recurring && (
                    <span className="inline-block text-xs font-semibold bg-gradient-to-r from-indigo-400 to-pink-400 text-white px-2 py-0.5 rounded-full ml-1">
                      {t.recurring}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-400 mb-1">{formatDate(a.activity_date)}</div>
                <div className="text-sm text-gray-500">
                  {a.start_time && a.end_time
                    ? `${a.start_time}–${a.end_time}`
                    : a.duration}
                </div>
                {a.note && <div className="text-gray-700 mt-1">{a.note}</div>}
                <div className="flex gap-2 mt-2">
                  <button
                    className="text-blue-500 hover:underline text-xs"
                    title={t.edit}
                    onClick={() => handleOpenEdit(a)}
                  >
                    {t.edit}
                  </button>
                  <button
                    className="text-red-500 hover:underline text-xs"
                    title={t.delete}
                    onClick={() => handleDelete(a)}
                  >
                    {t.delete}
                  </button>
                </div>
              </motion.li>
            );
          })}
        </motion.ul>
      </div>

      {/* Add Activity Modal */}
      <AnimatePresence>
        {showAddModal && (
          <EditActivityModal
            show={showAddModal}
            onClose={() => setShowAddModal(false)}
            activity={{
              activity: "",
              duration: "",
              note: "",
              category: "work",
              activity_date: selectedDate.toLocaleDateString("en-CA"),
              start_time: "",
              end_time: ""
            }}
            onSave={handleAddActivity}
            categories={categoryOptions}
          />
        )}
      </AnimatePresence>

      {/* Edit Activity Modal */}
      <AnimatePresence>
        {showEditModal && editActivity && (
          <EditActivityModal
            show={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setEditActivity(null);
            }}
            activity={editActivity}
            onSave={handleSaveEditActivity}
            categories={categoryOptions}
          />
        )}
      </AnimatePresence>
    </div>
  );
}